import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { getDiscount } from '@/utils/stripe-helpers'

type ExternalPaymentMethod = 'sequra'

export type Product = {
  productId: string
  priceId: string
  title: string
  subTitle: string
  amount: number
  period: string
  productTime: string
  academy: string
  external_pm: ExternalPaymentMethod[]
}
export type ProductDetail = {
  totalAmount: number
  subscriptionAmount: number
  period: string
  productTime: string
  productsList: Product[]
  canBeStudent: boolean
  isElite: boolean
}

export type Upselling = {
  [priceId: string]: {
    saving: number
    products: ProductDetail
  }
}

export type Promotion = {
  code: string
  promotionId: string
  couponId: string
  promotionAmount: number
  promotionPercentage: number
  promoApplyToProducts: string[]
}

export type CustomerDiscounts = {
  customerBalance: number
  customerSubDiscount: number
}

export type ProductFields = {
  selectedUpSelling: string
  promotion: Promotion
  products: ProductDetail
  upSellingProducts: Upselling
  currentProducts: ProductDetail
  external_pm: string[]
  totalAmount: number
  totalAmountWithDiscount: number
  customerDiscounts: CustomerDiscounts
}

export type ProductStore = ProductFields & {
  setPromotion(promotion: Promotion): void
  setUpselling(priceId: string): void
  setProducts(products: ProductDetail): void
  setStore(products: ProductDetail, upselling: Upselling): void
  setUpsellingProducts(products: Upselling): void
  setCustomerDiscounts(discounts: CustomerDiscounts): void
  reset(): void
}

const initialState = {
  selectedUpSelling: '',
  promotion: null,
  products: null,
  upSellingProducts: null,
  currentProducts: null,
  external_pm: [],
  totalAmount: 0,
  totalAmountWithDiscount: 0,
  customerDiscounts: null,
}

const findPaymentMethodForAllProds = (input): string[] => {
  const stringFindings = input.reduce((frequency, data) => {
    data.external_pm.forEach((paymentMethod) => {
      frequency[paymentMethod] = (frequency[paymentMethod] || 0) + 1
    })
    return frequency
  }, {})

  return Object.keys(stringFindings).filter((cadena) => {
    return stringFindings[cadena] === input.length
  })
}

export const useProductStore = create<ProductStore>()(
  persist(
    (set) => ({
      ...initialState,
      setPromotion(promotion: Promotion) {
        set((state) => {
          const product =
            state?.selectedUpSelling &&
            state.upSellingProducts[state.selectedUpSelling]?.products
              ? state.upSellingProducts[state.selectedUpSelling]?.products
              : state?.products
          const total = product?.totalAmount || 0
          const { total: totalWithDiscount } = getDiscount(
            product,
            promotion,
            state?.customerDiscounts
          )
          return {
            promotion,
            totalAmount: total,
            totalAmountWithDiscount: totalWithDiscount,
          }
        })
      },
      setUpselling(priceId: string) {
        set((state) => {
          const product = priceId
            ? state?.upSellingProducts[priceId]?.products
            : state?.products
          const total = product?.totalAmount || 0

          const { total: totalWithDiscount } = getDiscount(
            product,
            state?.promotion,
            state?.customerDiscounts
          )
          return {
            selectedUpSelling: priceId,
            totalAmount: total,
            totalAmountWithDiscount: totalWithDiscount,
            currentProducts: product,
            external_pm: findPaymentMethodForAllProds(product.productsList),
          }
        })
      },
      setStore(products: ProductDetail, upselling: Upselling) {
        set((state) => {
          const product =
            state.selectedUpSelling &&
            upselling[state.selectedUpSelling]?.products
              ? upselling[state.selectedUpSelling]?.products
              : products
          const total = product.totalAmount || 0

          const { total: totalWithDiscount } = getDiscount(
            product,
            state?.promotion,
            state?.customerDiscounts
          )
          return {
            ...state,
            products,
            upSellingProducts: upselling,
            currentProducts: product,
            external_pm: findPaymentMethodForAllProds(product.productsList),
            totalAmount: total,
            totalAmountWithDiscount: totalWithDiscount,
          }
        })
      },
      setProducts(products: ProductDetail) {
        set({ products, totalAmount: products.totalAmount })
      },
      setUpsellingProducts(products: Upselling) {
        set({ upSellingProducts: products })
      },
      setCustomerDiscounts(discounts: CustomerDiscounts) {
        set((state) => {
          const product = state?.currentProducts
          const total = product?.totalAmount || 0

          const { total: totalWithDiscount } = getDiscount(
            product,
            state?.promotion,
            discounts
          )
          return {
            totalAmount: total,
            totalAmountWithDiscount: totalWithDiscount,
            customerDiscounts: discounts,
          }
        })
      },
      reset: () => {
        set({ ...initialState })
      },
    }),
    {
      name: 'product-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
)
